import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { airportRowTitle, countryLabelFromCode, formatAirportOptionLabel, isRailStationName, useGetAirportsByNameQuery, } from '@massilia/queries';
import { AutoComplete, useDebounce } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import { PlaneLanding, PlaneTakeoff, TrainFront } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { DEFAULT_OPTION } from '../schemas/estimate/base-schemas.js';
import { FieldError } from './field-error.js';
export function AirportOnlyAutocompleteField({ errorsDisplayType = 'popover', label, mode, }) {
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 200);
    const { data, isLoading } = useGetAirportsByNameQuery({
        filter: 'active',
        fuzzy: debouncedSearchText || ' ',
    });
    const Icon = mode === 'departure' ? PlaneTakeoff : PlaneLanding;
    const options = useMemo(() => {
        if (!data?.airports)
            return [];
        return data.airports.map((airport) => ({
            description: [countryLabelFromCode(airport.city.countryCode), airport.city.name]
                .filter(Boolean)
                .join(' · '),
            icon: isRailStationName(airport.name) ? (_jsx(TrainFront, { className: "h-4 w-4 text-gray-light-500" })) : (_jsx(Icon, { className: "h-4 w-4 text-gray-light-500" })),
            label: formatAirportOptionLabel({
                airportName: airport.name,
                cityName: airport.city.name,
                iataCode: airport.iataCode,
            }),
            rowLabel: airportRowTitle(airport.name, airport.iataCode),
            trailing: (_jsx("span", { className: "shrink-0 rounded-md bg-brand-50 px-2 py-1 font-mono text-xs font-semibold tracking-widest text-brand-700 tabular-nums", children: airport.iataCode })),
            value: airport.iataCode,
        }));
    }, [data, Icon]);
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    const selectedOption = useMemo(() => {
        const currentValue = field.state.value?.value;
        if (!currentValue)
            return;
        const foundOption = options.find((opt) => opt.value === currentValue);
        if (foundOption)
            return foundOption;
        if (field.state.value?.label) {
            return field.state.value;
        }
        return;
    }, [options, field.state.value]);
    return (_jsxs("div", { className: "relative flex flex-col gap-2", children: [label && _jsx("label", { className: "text-sm font-medium", children: label }), errorsDisplayType === 'popover' && _jsx(FieldError, { asPopover: true, errors: errors }), _jsx(AutoComplete, { emptyMessage: "Aucun a\u00E9roport trouv\u00E9", icon: _jsx(Icon, { className: "h-4 w-4 text-gray-light-700" }), isLoading: isLoading, onBlurCallBack: () => {
                    field.handleBlur();
                }, onInputChange: (text) => {
                    setSearchText(text);
                }, onValueChange: (opt) => {
                    if (opt) {
                        field.handleChange({ label: opt.label, value: opt.value });
                    }
                    else {
                        field.handleChange(DEFAULT_OPTION);
                    }
                }, options: options, placeholder: mode === 'departure' ? 'Lieu de départ' : 'Destination', shouldFilter: false, value: selectedOption }), errorsDisplayType === 'bellow' && _jsx(FieldError, { errors: errors })] }));
}
